import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, Tab, Paper, Badge } from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { APIHOST } from '../../common';

interface AdminTabsProps {
  currentPath: string;
}

export default function AdminTabs({ currentPath }: AdminTabsProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token || !currentUser) {
          return;
        }

        const response = await fetch(`${APIHOST}/api/messages/unread`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.debug('Fetched unread messages count:', data.length);
          setUnreadCount(data.length || 0);
        }
      } catch (error) {
        console.error('Error fetching unread messages count:', error);
      }
    };

    fetchUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [currentUser]);

  const tabs = [
    { label: 'Hotels', path: '/admin' },
    { label: 'Conferences', path: '/admin/conferences' },
    { label: 'Orders', path: '/admin/orders' },
    { label: 'Messages', path: '/admin/messages' }
  ];

  const getCurrentTabIndex = () => {
    const index = tabs.findIndex(tab => tab.path === currentPath);
    return index >= 0 ? index : 0;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const targetPath = tabs[newValue]?.path;
    if (targetPath) {
      navigate(targetPath);
    }
  };

  return (
    <Paper sx={{ mb: 3 }}>
      <Tabs 
        value={getCurrentTabIndex()} 
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab, index) => {
          const isMessagesTab = tab.path === '/admin/messages';
          const tabLabel = isMessagesTab && unreadCount > 0 ? (
            <Badge badgeContent={unreadCount} color="error">
              {tab.label}
            </Badge>
          ) : tab.label;
          
          return (
            <Tab key={index} label={tabLabel} onClick={() => navigate(tab.path)} />
          );
        })}
      </Tabs>
    </Paper>
  );
}